import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import {
  System,
  Range,
  isNotNullOrUndefined,
  NodeType,
  getFirstAndLastMonthDays,
  getFirstAndLastYearDays,
  Inverter,
  ProductModelService,
} from 'ngx-core-services';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { ECharts } from 'echarts';
import { addDays, addMonths, format, startOfMonth, startOfYear } from 'date-fns';

import { PickerMode } from './monthly-yearly-energy-overview.model';
import { buildEnergyGraph, getAggregateEnergySeries, getEnergiesStatistics } from './monthly-yearly-energy-overview.graph';
import { DEFAULT_TELEMETRY_MODE, getChartLegendByNodeSelection, labels } from './monthly-yearly-energy-overview.constants';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { SystemsState, selectedSystemMetadataNodesType, selectedSystemMetadata, selectedSystemUid, requestSystemMonthlyYearlyEnergy, selectAzzurroHubHasAnySolarSensor, selectedSystemMonitorPref } from '../../store';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { selectSystemEnergyModeBySelectedSystem, selectSystemEnergyRangeBySelectedSystem, selectSystemEnergyStateBySelectedSystem,  selectedSystemMonthlyYearlyEnergyRequestStatus } from '../../store/selectors/monthly-yearly-energy.selectors';
import { exportToExcel } from '../utils/export-xlsx';

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-640': 640,
})
@Component({
  selector: 'lib-monthly-yearly-energy-overview',
  templateUrl: './monthly-yearly-energy-overview.component.html',
  styleUrls: ['./monthly-yearly-energy-overview.component.scss'],
})
export class MonthlyYearlyEnergyOverviewComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() locate: string = 'it-IT';

  @Input() todayMode: boolean | null = false;

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof labels{
    return this._labels;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  @Input() extraSourceOptions: NodeType[] = [];

  readonly sourceSelection$ = new BehaviorSubject<NodeType[]>(['solar']);
  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  readonly selectedMode$ = new BehaviorSubject<'month'|'year'>(DEFAULT_TELEMETRY_MODE);
  readonly selectedSystemUid$ = this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined());
  readonly systemMetadata$ = this.store.select(selectedSystemMetadata).pipe(isNotNullOrUndefined());
  readonly selectedSystemNodes$ = this.store.select(selectedSystemMetadataNodesType).pipe(isNotNullOrUndefined());
  readonly requestStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectedSystemMonthlyYearlyEnergyRequestStatus(instanceId)).pipe(isNotNullOrUndefined())),
  );
  readonly mode$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemEnergyModeBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );
  readonly range$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemEnergyRangeBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );
  readonly energies$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemEnergyStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );
  readonly requestSystemEnergyData$ = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
  ]).pipe(
    tap(([forceRefresh, systemId]) => {
      return { forceRefresh, systemId };
    }),
  );

  readonly requestIsPending$ = this.requestStatus$.pipe(
    map((status) => status === 'pending'),
    startWith(false),
    isNotNullOrUndefined(),
  );

  readonly isAzzurroHub$ = this.store.select(selectedSystemMonitorPref).pipe(
    map((monitoringType) => monitoringType === 'AzzurroHub'),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly hasSolarSensor$ = this.store.select(selectAzzurroHubHasAnySolarSensor);

  readonly baseSourceOptions$ = combineLatest([
    this.selectedSystemNodes$,
    this._deviceId$
  ]).pipe(
    switchMap(async ([systemSourceOption, deviceId]) => {
      if( deviceId ) {
        const product = new ProductModelService(deviceId);
        if (product.type?.name === 'meter' || product.type?.name === 'enerclic') {
          return ['grid'] as NodeType[];
        }
        if (product.type?.name !== 'inverter') {
          return [];
        }
        if(product.type.value === 'pv') {
          return ['solar'] as NodeType[];
        }
        return product.type.value === 'storage'
          ? ['battery'] as NodeType[]
          : ['solar', 'battery'] as NodeType[];

      }
      return systemSourceOption;
    }),
  );

  readonly sourceOptions$ = combineLatest([
    this.baseSourceOptions$,
    this.isAzzurroHub$,
    this.hasSolarSensor$,
    this._deviceId$,
  ]).pipe(
    map(([options, isAzzurroHub, hasSolarSensor, deviceId]) => {
      let allOptions = options;
      // deviceId is used on device detail block, if it's present we want to show only the relevant source option, but if it's not (system overview) and it's an AzzurroHub with solar sensor we want to show also irradiance
      if (isAzzurroHub && hasSolarSensor && !deviceId) {
        allOptions = [...allOptions, 'irradiance'] as NodeType[];
      }
      const extras = this.extraSourceOptions.filter(s => !allOptions.includes(s));
      return [...allOptions, ...extras] as NodeType[];
    }),
    tap((sourceOptions) => {
      if (sourceOptions.length) {
        this.sourceSelection$.next(sourceOptions);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly requestEnergyData$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._deviceId$,
    this.dateSelection$.pipe(startWith(this.dateSelection$.value)),
    this.selectedMode$.pipe(startWith(this.selectedMode$.value)),
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(async ([systemId, deviceId, dateSelection, mode, forceRefresh, instanceId]) => {
      let range;

      /**
       * If today mode is enabled, we will set the date selection to today
       */
      if ( this.todayMode ) {
        mode = 'month';
        dateSelection = format(new Date(), 'yyyy-MM-dd');
      }

      if ( mode === 'month') {
        const weekDays = getFirstAndLastMonthDays(dateSelection);
        range = { from: weekDays.from, to: weekDays.to };
      } else {
        const weekDays = getFirstAndLastYearDays(dateSelection);
        range = { from: weekDays.from, to: weekDays.to };
      }

      this.store.dispatch(requestSystemMonthlyYearlyEnergy(
        {
          systemId,
          instanceId,
          range,
          mode,
          forceRefresh,
          deviceId,
        }
      ));
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );


  readonly energyStatistics$ = combineLatest([
    this.sourceOptions$,
    this.energies$,
    this.mode$,
    this.range$,
  ]).pipe(
    map(([sourceOption, energies, mode, range]) => this.getEnergyStatistics(energies, range, mode, sourceOption)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly chartOpts$ = combineLatest([
    this.sourceSelection$,
    this.energies$,
    this.mode$,
    this.range$,
  ]).pipe(
    map(([sourceOption, energies, mode, range]) => this.getGraphOptions(energies, range, mode,sourceOption)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  chartInstances: ECharts[] = [];
  isLoading = true;

  _currentWidth = 0;
  set currentWidth(value: number) {
    if (value === this._currentWidth) {
      return;
    }

    this._currentWidth = value;
    this.chartInstances.forEach((chart) => {
      const dom = chart.getDom();
      if (dom) {
        dom.style.height = '100%';
        dom.style.minHeight = '350px';
      }
      chart.resize();
    });
  }
  get currentWidth() {
    return this._currentWidth;
  }

  constructor(
    readonly el: ElementRef,
    readonly store: Store<SystemsState>,
    readonly renderer: Renderer2,
  ) { }

  updateSourcesSelection(selectedItems: NodeType[] = this.sourceSelection$.value) {
    if (!selectedItems.length) {
      return;
    }
    this.sourceSelection$.next(selectedItems);
  }

  getEnergyStatistics(data: Partial<Inverter>[], range: Range, mode: PickerMode, energyToShow: NodeType[]){
    const { energyAggregates } = getAggregateEnergySeries(data, range, mode);
    return getEnergiesStatistics(energyAggregates, energyToShow);
  }

  getGraphOptions(data: Partial<Inverter>[], range: Range, mode: PickerMode, sourceSelection: NodeType[]) {
    const { energyAggregates, periodDates } = getAggregateEnergySeries(data, range, mode);
    const selectedSeries = getChartLegendByNodeSelection(sourceSelection);
    const hasValues = Object.values(energyAggregates).some(({ values }) => values?.some((energyValue) => energyValue !== null));

    const graphsConfig = buildEnergyGraph(energyAggregates, periodDates, selectedSeries);

    return {
      graph: mode === 'month' ? graphsConfig.month : graphsConfig.year,
      reducedGraph: mode === 'month' ? graphsConfig.weeks : graphsConfig.halfYear,
      hasValues,
    };
  }

  changeSelectMode(mode: string) {
    this.selectedMode$.next(mode as 'month' | 'year');
  }

  onChartInit(ec: ECharts) {
    this.chartInstances.push(ec);
  }

  onExport(series: any) {
    const seriesData = series.map((s: any) => ({
      ...s,
      data: s.data.map((value: any, index: number) => [index, value]),
    }));
    const date = this.dateSelection$.value;
    const selectedMode = this.selectedMode$.value;

    const timeTransformFn = (valuex: number) => {
      if (selectedMode === 'month') {
        const _startOfMonth = startOfMonth(new Date(date));
        return format(addDays(_startOfMonth, valuex), 'yyyy-MM-dd');
      }

      const _startOfYear = startOfYear(new Date(date));
      return format(addMonths(_startOfYear, valuex), 'yyyy-MM');
    };

    const dataTransformFn = (name: string, value: any) => {
      return {
        name,
        value: `${Math.abs(value).toFixed(2)} kWh`,
      };
    }

    const deviceId = this._deviceId$.value;
    const filename = `${deviceId ? deviceId : 'system'}_${date}_${selectedMode}.xlsx`;

    const labels: Record<string, string> = {};
    if (seriesData.find((s: any) => s.name === 'EG1')) {
      labels['EG1'] = this.labels.statisticsGeneratedTitle;
    }
    if (seriesData.find((s: any) => s.name === 'EG2')) {
      labels['EG2'] = this.labels.statisticsExportSubtitle;
    }
    if (seriesData.find((s: any) => s.name === 'EG3')) {
      labels['EG3'] = this.labels.statisticsImportSubtitle;
    }
    if (seriesData.find((s: any) => s.name === 'EG4')) {
      labels['EG4'] = this.labels.statisticsConsumedTitle;
    }
    if (seriesData.find((s: any) => s.name === 'EG5')) {
      labels['EG5'] = this.labels.statisticsChargeSubtitle;
    }
    if (seriesData.find((s: any) => s.name === 'EG6')) {
      labels['EG6'] = this.labels.statisticsDischargeSubtitle;
    }

    exportToExcel(seriesData, labels, timeTransformFn, filename, selectedMode, dataTransformFn);
  }
}
